/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
	Button,
	Table,
	Switch,
	Modal,
	Input,
	Upload,
	Select,
	notification,
	Tag,
	Tooltip,
	Alert,
	Spin,
	Typography,
	Card,
	Segmented,
	Empty,
} from 'antd';
import {
	PlusOutlined,
	LinkOutlined,
	UploadOutlined,
	ReloadOutlined,
	SyncOutlined,
	EditOutlined,
	EyeOutlined,
	CopyOutlined,
	DeleteFilled,
	LoadingOutlined,
	AppstoreOutlined,
	UnorderedListOutlined,
	DeleteOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd/es/upload';
import { useMutation } from '@tanstack/react-query';
import {
	crawlUrl,
	deleteKnowledgeBase,
	generateKnowledgeBase,
	manualSyncKnowledgeBase,
	updateKnowledgeBaseActivation,
	updateKnowledgeBaseContent,
} from '@/api/user';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { FeatureFlag } from 'src/enums/EFeatureFlags';
const { Option } = Select;
import { InfoOutlined } from '@mui/icons-material';
import { LabelWithDescription } from '@/components/LabelWithdescription';
import { useMobileDeviceCheck } from 'src/common/hooks/useMobile';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgent } from '@/store/account/selector';
import { updateCampaignVariables } from '@/api/novi/CamapaignPortalApis';
import DOMPurify from 'dompurify';
import TextEditor from '@/components/TextEditor/TextEditor';
import { getAllVariables } from '@/store/novi/variables/selector';
import { Variable } from '@/types/TVariables';
import { setVariable } from '@/store/novi/variables/slice';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { formatTextWithHighlightedVariables } from '@/utils/textFormatting';
import { KnowledgeBaseItemProps } from '@/interfaces/index';
import { checkGptLatency } from '@/utils/helper';
import { getSelectedAgentId } from '@/store/account/selector';
import Paragraph from 'antd/es/typography/Paragraph';
import { isObject, isArray } from 'lodash';
import { safeJSONParse } from '@/utils/helper';

const { Text } = Typography;

interface KnowledgeBaseCardProps {
	item: KnowledgeBaseItemProps;
	index: number;
	dataNumber: string;
	selectedAgentId: string;
	refetch: () => void;
	openViewModal: (text: string) => void;
	openEditModal: (index: number, content: string) => void;
	updateActivationMutation: any;
	manualSyncMutation: any;
	deleteMutation: any;
	updateContentMutation: any;
	setLoading: (loading: boolean) => void;
	isLoading: boolean;
	openDeleteModal: (index: number) => void;
}

const KnowledgeBaseCard: React.FC<KnowledgeBaseCardProps> = ({
	item,
	index,
	dataNumber,
	selectedAgentId,
	refetch,
	openViewModal,
	openEditModal,
	updateActivationMutation,
	manualSyncMutation,
	deleteMutation,
	setLoading,
	isLoading,
	openDeleteModal,
}) => {
	const getColor = (type: string) => {
		switch (type) {
			case 'text':
				return {
					base: 'blue',
					border: 'border-blue-300',
					hoverBorder: 'hover:border-blue-400',
				};
			case 'link':
				return {
					base: 'green',
					border: 'border-green-300',
					hoverBorder: 'hover:border-green-400',
				};
			case 'file':
				return {
					base: 'purple',
					border: 'border-purple-300',
					hoverBorder: 'hover:border-purple-400',
				};
			default:
				return {
					base: 'red',
					border: 'border-red-300',
					hoverBorder: 'hover:border-red-400',
					hoverBg: 'hover:bg-red-50',
				};
		}
	};

	const color = getColor(item.type);
	const statusColor = item.isActive ? 'text-blue-700 bg-blue-100' : 'text-red-700 bg-red-100';

	const handleToggleActivation = (checked: boolean) => {
		setLoading(true);
		updateActivationMutation.mutate(
			{
				number: dataNumber,
				entryIndex: index,
				isActive: checked,
				agentId: selectedAgentId,
			},
			{
				onSettled: () => setLoading(false),
				onSuccess: () => refetch(),
				onError: (error: any) => {
					notification.error({
						message: 'Failed to update status',
						description: error?.message || 'An error occurred.',
					});
				},
			},
		);
	};

	const handleManualSync = () => {
		setLoading(true);
		manualSyncMutation.mutate(
			{
				number: dataNumber,
				entryIndex: index,
				entry: JSON.stringify(item),
				agentId: selectedAgentId,
			},
			{
				onSettled: () => setLoading(false),
				onError: (error: any) => {
					notification.error({
						message: 'Failed to sync',
						description: error?.message || 'An unknown error occurred during sync.',
					});
				},
			},
		);
	};

	const displayContent = item.content
		? DOMPurify.sanitize(item.content)
				.replace(/<[^>]*>?/gm, '')
				.substring(0, 200) + (item.content.length > 200 ? '...' : '')
		: '';

	const handleViewOrEdit = (type: string, status: string) => {
		if (status === 'processing') {
			return;
		}

		if (type === 'text') {
			openEditModal(index, item.content);
		} else {
			openViewModal(item.content);
		}
	};

	return (
		<div
			className="relative flex flex-col w-full"
			onClick={() => {
				handleViewOrEdit(item.type, item.status);
			}}
		>
			{/* Card Body */}
			<Card
				hoverable
				bordered
				className={`rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border-2  ${color.border} ${color.hoverBorder} hover:scale-105 hover:-translate-y-1 w-full max-w-xs md:max-w-sm lg:max-w-md xl:max-w-none`}
				style={{
					height: 280,
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
				}}
				bodyStyle={{
					display: 'flex',
					flexDirection: 'column',
					padding: '1.25rem',
					height: '100%',
				}}
			>
				<div>
					<div className="flex justify-between mb-2 items-baseline">
						<Tag
							className={`px-2 py-1 border-0 rounded-full font-medium text-sm bg-opacity-40 text-${color.base}-700 bg-${color.base}-100`}
						>
							{item.type.charAt(0).toUpperCase() + item.type.slice(1)}
						</Tag>
						<div className="flex items-center">
							<Switch
								checked={item.isActive}
								onChange={(checked, e) => {
									e.stopPropagation();
									handleToggleActivation(checked);
								}}
								className={`transition ${item.isActive ? '!bg-blue-600' : '!bg-red-300'}`}
								size="small"
							/>
							<Tag
								className={`border-0 rounded-full font-medium px-2 py-0.5 text-sm bg-opacity-40 ${statusColor}`}
							>
								{item.isActive ? 'Active' : 'Inactive'}
							</Tag>
							<Button
								type="text"
								icon={<DeleteFilled />}
								onClick={(event) => {
									event.stopPropagation();
									openDeleteModal(index);
								}}
								className="mx-0 text-red-600 hover:text-red-700 hover:bg-red-50"
								size="small"
								title="Delete"
							/>
						</div>
					</div>

					<Text strong className="block mb-1 text-gray-800 text-sm">
						Content:
					</Text>
					<div className="relative min-h-[4rem] mb-1">
						<Paragraph className="text-gray-700 text-sm overflow-hidden line-clamp-6">
							{item.content ? (
								displayContent
							) : (
								<div className="flex items-center gap-1">
									<Tag className="bg-amber-50 text-amber-700 border-0 font-medium px-2 py-0.5">
										Processing
									</Tag>
									<Tooltip title="The AI is working on generating relevant data from your given resource. This can take 3-5 minutes depending on the amount of data. You'll be able to see it here once complete. Use the refresh button to check the status.">
										<InfoOutlined className="text-gray-400 text-xs cursor-help" />
									</Tooltip>
								</div>
							)}
						</Paragraph>
					</div>
				</div>
				<div className="flex flex-wrap lg:flex-row items-center justify-center py-1 max-w-full">
					{item.syncType && (
						<Tag className="bg-green-50 text-green-700 border-0 font-small px-1 py-1 rounded-3xl">
							Auto Sync:{' '}
							<strong>{item.syncType.charAt(0).toUpperCase() + item.syncType.slice(1)}</strong>
						</Tag>
					)}
					{item.type === 'link' && item.content && (
						<Button
							type="default"
							icon={<SyncOutlined />}
							onClick={(event) => {
								event.stopPropagation();
								handleManualSync();
							}}
							className="border-blue-100 bg-blue-50 hover:bg-blue-100 hover:border-blue-200 text-blue-700 h-xl text-xs"
							size="small"
						>
							Sync Now
						</Button>
					)}
				</div>
			</Card>
		</div>
	);
};

export const KnowledgeBaseRevamped: React.FC<{
	data: any;
	refetch: () => void;
	isNoviUser: boolean;
}> = ({ data, refetch, isNoviUser }) => {
	const dispatch = useAppDispatch();
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	const { isMobile } = useMobileDeviceCheck();
	const isKnowledgeBaseContentLatencyAlertEnabled = useFeatureFlag(
		FeatureFlag.KNOWLEDGEBASE_CONTENT_LATENCY_ALERTS,
	);
	const MAX_TEXT_CHARACTERS = 25000;
	const [isTextModalVisible, setIsTextModalVisible] = useState(false);
	const [isUrlModalVisible, setIsUrlModalVisible] = useState(false);
	const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
	const [isEditModalVisible, setIsEditModalVisible] = useState(false);
	const [textInput, setTextInput] = useState('');
	const [urlInput, setUrlInput] = useState('');
	const [syncType, setSyncType] = useState('');
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [loading, setLoading] = useState(false);
	const [editContentIndex, setEditContentIndex] = useState<number | null>(null);
	const [editTextInput, setEditTextInput] = useState('');
	const [selectedText, setSelectedText] = useState('');
	const [isViewModalVisible, setIsViewModalVisible] = useState(false);
	const [urlTransformInstructions, setUrlTransformInstructions] = useState('');
	const [documentTransformInstructions, setDocumentTransformInstructions] = useState('');
	const [gptLatencyState, setGptLatencyState] = useState<{
		latency: boolean;
		type: 'high' | 'very high' | 'no alert';
	}>({
		latency: false,
		type: 'no alert',
	});
	const [crawlLoading, setCrawlLoading] = useState(false);
	const [crawledUrls, setCrawledUrls] = useState<string[]>([]);
	const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
	const [isCrawlStep, setIsCrawlStep] = useState(true);
	const [parentUrl, setParentUrl] = useState('');
	const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
	const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

	const linkCount = data?.knowledgeBase?.filter((item: any) => item.type === 'link')?.length ?? 0;

	useEffect(() => {
		if (!data || !isKnowledgeBaseContentLatencyAlertEnabled) {
			return;
		}
		const newKnowledgeBaseData =
			!!data?.knowledgeBase && data?.knowledgeBase?.length > 0
				? data?.knowledgeBase
						?.map(
							(knowledgeBaseItem: KnowledgeBaseItemProps) =>
								(knowledgeBaseItem?.isActive && knowledgeBaseItem?.content) || '',
						)
						?.join(' ')
				: '';

		setGptLatencyState(checkGptLatency(newKnowledgeBaseData));
	}, [data, isKnowledgeBaseContentLatencyAlertEnabled]);

	let selectedCampaign: any;
	if (isNoviUser) {
		selectedCampaign = useAppSelector(getSelectedAgent);
	}

	const variables = useAppSelector(getAllVariables);

	useEffect(() => {
		if (selectedCampaign?.variables?.length) {
			try {
				const parsedVariables = safeJSONParse(selectedCampaign.variables, {});
				Object.entries(parsedVariables).forEach(([key, value]) => {
					dispatch(setVariable({ name: key, data: value as Variable }));
				});
			} catch (error) {
				console.error('Error setting variables', error);
			}
		}
	}, [selectedCampaign, dispatch]);

	const { mutate: updateVariables } = useMutation({
		mutationKey: ['update-variables'],
		mutationFn: () => updateCampaignVariables(selectedCampaign?.number, variables, 'Client'),
		onSuccess: () => {
			refetch();
			notification.success({
				key: 'form-success',
				message: 'Variables have been saved successfully!',
				duration: 3,
			});
		},
	});

	const handleVariableChange = (updatedVariables: any) => {
		Object.entries(updatedVariables).forEach(([key, value]) => {
			dispatch(setVariable({ name: key, data: value as Variable }));
		});

		updateVariables();
	};

	const GenerateKnowledgeBase = useMutation({
		mutationKey: ['generate-knowledge-base'],
		mutationFn: (formData: FormData) => generateKnowledgeBase(formData),
		onSuccess: () => {
			refetch();
			notification.success({
				message: 'Knowledge base entry created successfully',
			});
		},
		onError: (error) => {
			console.error(error);
			notification.error({
				message: `Failed to create knowledge base entry: ${error.message}`,
			});
		},
	});

	const ManualSyncKnowledgeBase = useMutation({
		mutationKey: ['manual-sync-knowledge-base'],
		mutationFn: (payload: { number: string; entryIndex: number; entry: any; agentId: string }) =>
			manualSyncKnowledgeBase(payload),
		onSuccess: () => {
			refetch();
			setLoading(false);
			notification.success({
				message: 'Manual sync successful',
			});
		},
		onError: () => {
			setLoading(false);
			notification.error({
				message: 'Failed to sync',
			});
		},
	});

	const UpdateKnowledgeBaseActivation = useMutation({
		mutationKey: ['update-knowledge-base-activation'],
		mutationFn: (payload: {
			number: string;
			entryIndex: number;
			isActive: boolean;
			agentId: string;
		}) => updateKnowledgeBaseActivation(payload),
		onSuccess: () => {
			refetch();
			setLoading(false);
			notification.success({
				message: 'Status updated successfully',
			});
		},
		onError: () => {
			setLoading(false);
			notification.error({
				message: 'Failed to update status',
			});
		},
	});

	const UpdateKnowledgeBaseContent = useMutation({
		mutationKey: ['update-knowledge-base-content'],
		mutationFn: (payload: {
			number: string;
			entryIndex: number;
			content: string;
			agentId: string;
		}) => updateKnowledgeBaseContent(payload),
		onSuccess: () => {
			refetch();
			notification.success({
				message: 'Content updated successfully',
			});
		},
		onError: () => {
			notification.error({
				message: 'Failed to update content',
			});
		},
	});

	const DeleteKnowledgeBase = useMutation({
		mutationKey: ['delete-knowledge-base'],
		mutationFn: (payload: { number: string; entryIndex: number; agentId: string }) =>
			deleteKnowledgeBase(payload),
		onSuccess: () => {
			refetch();
			setLoading(false);
			notification.success({
				message: 'Entry deleted successfully',
			});
		},
		onError: () => {
			setLoading(false);
			notification.error({
				message: 'Failed to delete entry',
			});
		},
	});

	const CrawlUrl = useMutation({
		mutationKey: ['crawl-url'],
		mutationFn: (payload: { url: string; depth?: number }) => crawlUrl(payload),
		onSuccess: () => {
			notification.success({
				message: 'URL crawled successfully',
			});
		},
	});

	const openViewModal = (text: string) => {
		setSelectedText(text);
		setIsViewModalVisible(true);
	};

	const openEditModal = (index: number, content: string) => {
		setEditContentIndex(index);
		setEditTextInput(content);
		setIsEditModalVisible(true);
	};

	const handleEditSave = () => {
		if (editTextInput?.toString().trim() === '') {
			notification.error({
				message: 'Please enter some content to update',
				duration: 3,
			});
			return;
		}
		setLoading(true);
		UpdateKnowledgeBaseContent.mutate(
			{
				number: data.number,
				entryIndex: editContentIndex,
				content: editTextInput,
				agentId: selectedAgentId as string,
			},
			{
				onSettled: () => {
					setIsEditModalVisible(false);
					setEditTextInput('');
					setEditContentIndex(null);
					setLoading(false);
				},
			},
		);
	};

	const handleAddText = () => {
		if (!textInput) {
			return;
		}
		setLoading(true);

		const formData = new FormData();
		formData.append('number', data.number);
		formData.append('textInput', textInput);
		formData.append('agentId', selectedAgentId as string);

		GenerateKnowledgeBase.mutate(formData, {
			onSettled: () => {
				setLoading(false);
				setIsTextModalVisible(false);
				setTextInput('');
			},
		});
	};

	const handleFetchUrl = () => {
		if (isCrawlStep) {
			if (!urlInput) {
				return;
			}
			setCrawlLoading(true);
			setParentUrl(urlInput);

			CrawlUrl.mutate(
				{ url: urlInput, depth: 1 },
				{
					onSuccess: (data) => {
						setCrawlLoading(false);
						const crawledData = data as {
							pages: { anchorLinks: string[]; error: any; url: string }[];
						};
						const crawledPages = Object.values(crawledData?.pages); // convert object to array

						if (Array.isArray(crawledPages) && crawledPages?.length > 0) {
							setCrawledUrls(crawledPages.map((page) => page.url));

							setSyncType('weekly');
							setSelectedUrls([]);
							setIsCrawlStep(false);
						} else {
							notification.error({
								message: 'Failed to crawl URL',
								description: 'No valid URLs found or invalid response received',
							});
						}
					},
					onError: (error) => {
						console.error(error);
						setCrawlLoading(false);
						notification.error({
							message: 'Failed to crawl URL',
							description: 'No valid URLs found or invalid response received',
						});
					},
				},
			);
		} else {
			if (selectedUrls.length === 0) {
				notification.error({
					message: 'No URLs selected',
					description: 'Please select at least one URL to add to your knowledge base.',
				});
				return;
			}

			if (!syncType) {
				notification.error({
					message: 'Missing information',
					description: 'Please select an auto-update frequency.',
				});
				return;
			}

			setLoading(true);

			const formData = new FormData();
			formData.append('number', data.number);
			formData.append('url', parentUrl); // Send the main URL that was crawled
			formData.append('selectedUrls', JSON.stringify(selectedUrls)); // Send the selected URLs separately
			formData.append('syncType', syncType);
			if (urlTransformInstructions) {
				formData.append('transformInstructions', urlTransformInstructions);
			}

			GenerateKnowledgeBase.mutate(formData, {
				onSuccess: () => {
					resetUrlModal();
				},
				onError: (error) => {
					console.error(error);
					setLoading(false);
				},
			});
		}
	};

	const resetUrlModal = () => {
		setLoading(false);
		setIsUrlModalVisible(false);
		setUrlInput('');
		setSyncType('');
		setUrlTransformInstructions('');
		setCrawledUrls([]);
		setSelectedUrls([]);
		setIsCrawlStep(true);
		setParentUrl('');
	};

	const handleUrlSelectionChange = (url: string) => {
		setSelectedUrls((prev) => {
			if (prev.includes(url)) {
				return prev.filter((u) => u !== url);
			} else {
				// Check if adding would exceed the maximum limit of 3
				if (prev.length >= 3) {
					notification.warning({
						message: 'Selection limit reached',
						description: 'You can select a maximum of 3 URLs at a time.',
					});
					return prev;
				}
				return [...prev, url];
			}
		});
	};

	const uploadProps: UploadProps = {
		beforeUpload: (file) => {
			const isAllowedType =
				file.type === 'application/pdf' ||
				file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
				file.type === 'text/plain';

			if (!isAllowedType) {
				notification.error({
					message: 'You can only upload PDF, DOCX, or TXT files.',
				});
				return Upload.LIST_IGNORE;
			}

			setSelectedFile(file);
			setIsUploadModalVisible(true);
			return false;
		},
	};

	const handleUploadFile = () => {
		if (!selectedFile) {
			return;
		}
		setLoading(true);

		const formData = new FormData();
		formData.append('number', data.number);
		formData.append('files', selectedFile);
		if (documentTransformInstructions) {
			formData.append('transformInstructions', documentTransformInstructions);
		}

		GenerateKnowledgeBase.mutate(formData, {
			onSettled: () => {
				setLoading(false);
				setIsUploadModalVisible(false);
				setSelectedFile(null);
				setDocumentTransformInstructions('');
			},
		});
	};

	// Polling mechanism for processing entries
	useEffect(() => {
		const hasProcessingEntries = data?.knowledgeBase?.some(
			(item: any) => item.status === 'processing',
		);

		if (!hasProcessingEntries) {
			return;
		}

		const intervalId = setInterval(() => {
			refetch();
		}, 10000); // Poll every 10 seconds

		return () => {
			clearInterval(intervalId);
		};
	}, [data?.knowledgeBase, refetch]);

	const openDeleteModal = (index: number) => {
		setDeleteIndex(index);
		setIsDeleteModalVisible(true);
	};

	const handleDelete = () => {
		if (deleteIndex === null) return;
		let indexToDelete = deleteIndex;

		// let structure data index
		let structureDataIndex = data?.knowledgeBase?.findIndex((item: any) => item.structuredData);
		if (structureDataIndex !== -1 && deleteIndex >= structureDataIndex) {
			indexToDelete = indexToDelete + 1;
		}

		setLoading(true);
		DeleteKnowledgeBase.mutate(
			{
				number: data.number,
				entryIndex: indexToDelete,
				agentId: selectedAgentId as string,
			},
			{
				onSettled: () => {
					setLoading(false);
					setIsDeleteModalVisible(false);
					setDeleteIndex(null);
				},
				onError: (error: any) => {
					notification.error({
						message: 'Failed to delete entry',
						description: error?.message || 'An unknown error occurred while deleting entry.',
					});
				},
			},
		);
	};

	return (
		<Spin
			spinning={!data}
			indicator={
				<LoadingOutlined data-testid="main-loading-indicator" style={{ fontSize: '48px' }} spin />
			}
		>
			{isKnowledgeBaseContentLatencyAlertEnabled && gptLatencyState?.latency && (
				<Alert
					message={
						gptLatencyState?.type === 'high'
							? 'Large Knowledge Base Detected – Slower Response Expected'
							: 'Very Large Knowledge Base Detected – Expect Delays'
					}
					description="The provided content is large, which may lead to slower response times.
            Consider trimming unnecessary details for a faster experience."
					type="warning"
					showIcon
					className="mb-6"
				/>
			)}

			<div className="bg-white rounded-2xl shadow-sm border border-gray-100">
				{/* Header Section */}
				<div className="p-8 border-b border-gray-100">
					<p className="text-gray-600 text-base mb-6">
						Provide your business information through <span className="font-medium">text</span>,{' '}
						<span className="font-medium">website URLs</span>, or{' '}
						<span className="font-medium">documents</span>. Your AI can automatically refresh data
						from website links periodically.
					</p>

					{/* Action Buttons */}
					<div className="flex flex-wrap gap-3">
						<Tooltip title="Add information through simple text. Maximum three text knowledge bases allowed. Keep them concise for better results.">
							<Button
								type="default"
								icon={<PlusOutlined />}
								onClick={() => setIsTextModalVisible(true)}
								className="flex items-center border-blue-100 bg-blue-50 hover:bg-blue-100 hover:border-blue-200 text-blue-700"
							>
								Add Text
							</Button>
						</Tooltip>

						<Tooltip
							title={
								linkCount >= 3
									? 'You have reached the maximum limit of 3 URLs. Please remove some URLs or upgrade to a higher plan.'
									: 'Let AI extract information from your website pages. Supports automatic periodic updates. Maximum 3 URLs allowed.'
							}
						>
							<Button
								type="default"
								icon={<LinkOutlined />}
								onClick={() => setIsUrlModalVisible(true)}
								disabled={linkCount >= 3}
								className="flex items-center border-blue-100 bg-blue-50 hover:bg-blue-100 hover:border-blue-200 text-blue-700"
							>
								{isMobile ? 'Add URL' : 'Add Website URL'}
							</Button>
						</Tooltip>

						<Tooltip title="Upload documents (PDF, DOCX, TXT) for AI to extract information. Maximum 4 documents with 10,000 words limit.">
							<Upload {...uploadProps} showUploadList={false} accept=".pdf,.docx,.txt">
								<Button
									type="default"
									icon={<UploadOutlined />}
									className="flex items-center border-blue-100 bg-blue-50 hover:bg-blue-100 hover:border-blue-200 text-blue-700"
								>
									{isMobile ? 'Add Document' : 'Upload Document'}
								</Button>
							</Upload>
						</Tooltip>

						<Button
							type="default"
							icon={<ReloadOutlined />}
							onClick={refetch}
							className="flex items-center ml-auto hover:text-blue-700 hover:border-blue-300"
						>
							Refresh
						</Button>
					</div>
				</div>

				{/* Table and Card Section */}
				<div className="p-8">
					{data?.knowledgeBase?.length <= 0 ? (
						<>
							<Empty />
						</>
					) : (
						<Spin className="flex justify-center" spinning={loading}>
							<div className="grid grid-cols-1 justify-center sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
								{data?.knowledgeBase
									?.filter((item: KnowledgeBaseItemProps) => {
										return (
											(item.content ? !isObject(item.content) || isArray(item.content) : true) &&
											!(item as any)?.structuredData
										);
									})
									.map((item: KnowledgeBaseItemProps, index: number) => {
										return (
											<KnowledgeBaseCard
												key={index}
												item={item}
												index={index}
												dataNumber={data.number}
												selectedAgentId={selectedAgentId as string}
												refetch={refetch}
												openViewModal={openViewModal}
												openEditModal={openEditModal}
												updateActivationMutation={UpdateKnowledgeBaseActivation}
												manualSyncMutation={ManualSyncKnowledgeBase}
												deleteMutation={DeleteKnowledgeBase}
												updateContentMutation={UpdateKnowledgeBaseContent}
												setLoading={setLoading}
												isLoading={loading}
												openDeleteModal={openDeleteModal}
											/>
										);
									})}
							</div>
						</Spin>
					)}
				</div>

				{/* Modals */}
				<Modal
					title={
						<div className="flex items-center gap-3">
							<PlusOutlined className="text-blue-600" />
							<span className="text-lg font-semibold">Add Text Knowledge Base</span>
						</div>
					}
					open={isTextModalVisible}
					onOk={handleAddText}
					onCancel={() => setIsTextModalVisible(false)}
					confirmLoading={loading}
					okText="Add"
					className="[&_.ant-modal-content]:p-6 
					[&_.ant-btn-primary]:bg-blue-600 
					[&_.ant-btn-primary]:border-blue-600 
					[&_.ant-btn-primary:hover]:bg-blue-700 
					[&_.ant-btn-primary:hover]:border-blue-700
					[&_.ant-btn-default]:border-gray-200
					[&_.ant-btn-default:hover]:border-blue-600"
				>
					<div className="py-4">
						{isNoviUser ? (
							<TextEditor
								placeholder="Enter your business information"
								value={textInput}
								variables={variables}
								onChange={(text, variables) => {
									if (text) {
										setTextInput(text);
									}
									if (variables) {
										handleVariableChange(variables);
									}
								}}
							/>
						) : (
							<Input.TextArea
								autoSize={{ minRows: 8 }}
								maxLength={MAX_TEXT_CHARACTERS}
								value={textInput}
								onChange={(e) => setTextInput(e.target.value)}
								placeholder="Enter your business information"
								showCount
								className="mt-4"
							/>
						)}
					</div>
				</Modal>

				<Modal
					title={
						<div className="flex items-center gap-3">
							<LinkOutlined className="text-blue-600" />
							<span className="text-lg font-semibold">
								{isCrawlStep ? 'Crawl Website URL' : 'Select URLs to Add'}
							</span>
						</div>
					}
					open={isUrlModalVisible}
					onOk={handleFetchUrl}
					onCancel={() => (isCrawlStep ? resetUrlModal() : setIsCrawlStep(true))}
					confirmLoading={crawlLoading || loading}
					okText={isCrawlStep ? 'Crawl' : 'Add Selected URLs'}
					cancelText={isCrawlStep ? 'Cancel' : 'Back'}
					className="[&_.ant-modal-content]:p-6 
					[&_.ant-btn-primary]:bg-blue-600 
					[&_.ant-btn-primary]:border-blue-600 
					[&_.ant-btn-primary:hover]:bg-blue-700 
					[&_.ant-btn-primary:hover]:border-blue-700
					[&_.ant-btn-default]:border-gray-200
					[&_.ant-btn-default:hover]:border-blue-600"
					width={isCrawlStep ? 520 : 700}
				>
					<div className="py-4">
						{isCrawlStep ? (
							// Step 1: Enter URL and crawl depth
							<>
								<div className="mb-6">
									<LabelWithDescription label="Website URL" />
									<Input
										value={urlInput}
										onChange={(e) => setUrlInput(e.target.value)}
										placeholder="https://yourwebsite.com"
										className="mt-2"
										type="url"
										required
									/>
								</div>
							</>
						) : (
							// Step 2: Select URLs to add
							<>
								<div className="mb-4">
									<Alert
										message="Select URLs to add to Knowledge Base"
										description={
											<div>
												Choose which URLs contain the most relevant information for your business.
												You can select multiple URLs. It is recommended to specify sub-pages of your
												website instead of the main page. For example,{' '}
												<a
													href="https://www.bbc.com/sport"
													target="_blank"
													rel="noopener noreferrer"
													className="underline"
												>
													https://www.bbc.com/sport
												</a>{' '}
												instead of{' '}
												<a
													href="https://www.bbc.com"
													target="_blank"
													rel="noopener noreferrer"
													className="underline"
												>
													https://www.bbc.com
												</a>
												.
											</div>
										}
										type="info"
										showIcon
										className="mb-4"
									/>

									<div className="flex justify-between items-center mb-2">
										<Text strong>Found URLs ({crawledUrls.length})</Text>
										<Text type="secondary" className="mr-2">
											Selected: {selectedUrls.length}/3
										</Text>
									</div>

									<div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md p-2 mb-4">
										{/* Parent URL is always first and highlighted */}
										<div className="flex items-center p-2 bg-blue-50 rounded mb-1">
											<Switch
												checked={selectedUrls.includes(parentUrl)}
												onChange={() => handleUrlSelectionChange(parentUrl)}
												size="small"
												disabled={!selectedUrls.includes(parentUrl) && selectedUrls.length >= 3}
											/>
											<Text strong className="ml-2 truncate" title={parentUrl}>
												{parentUrl?.length > 70 ? parentUrl.slice(0, 70) + '...' : parentUrl}{' '}
												<Tag color="blue">Main URL</Tag>
											</Text>
										</div>

										{/* Child URLs */}
										{crawledUrls.map((url, index) => (
											<div key={index} className="flex items-center p-2 hover:bg-gray-50 rounded">
												<Switch
													checked={selectedUrls.includes(url)}
													onChange={() => handleUrlSelectionChange(url)}
													size="small"
													disabled={!selectedUrls.includes(url) && selectedUrls.length >= 3}
												/>
												<Text className="ml-2 truncate" title={url}>
													{url}
												</Text>
											</div>
										))}
									</div>
								</div>

								<div className="mb-6">
									<LabelWithDescription label="Auto-Update Frequency" />
									<Select
										placeholder="Select update frequency"
										className="w-full mt-2"
										value={syncType}
										onChange={(value) => setSyncType(value)}
									>
										<Option value="none">No auto-update</Option>
										<Option value="daily">Daily</Option>
										<Option value="weekly">Weekly</Option>
										<Option value="every 10 days">Every 10 days</Option>
										<Option value="every 2 weeks">Every 2 weeks</Option>
									</Select>
								</div>

								<div className="mt-6">
									<div className="flex items-center gap-2">
										<LabelWithDescription label="Transform Instructions" />
										<Tooltip title="Specify how the AI should process this content. You can instruct it to extract specific parts, ignore certain sections, or limit the amount of data extracted. Example: 'Only extract pricing information' or 'Ignore FAQ section'">
											<InfoOutlined className="text-gray-400 text-sm cursor-help" />
										</Tooltip>
									</div>
									<Input.TextArea
										placeholder="Optional: Provide instructions on how to extract or transform the content."
										className="mt-2"
										autoSize={{ minRows: 3, maxRows: 5 }}
										value={urlTransformInstructions}
										onChange={(e) => setUrlTransformInstructions(e.target.value)}
									/>
								</div>
							</>
						)}
					</div>
				</Modal>
				<Modal
					title={
						<div className="flex items-center gap-3 pt-8 mx-4 md:pt-0">
							<EditOutlined className="text-blue-600" />
							<span className="text-lg font-semibold">Edit Text</span>
						</div>
					}
					confirmLoading={loading}
					open={isEditModalVisible}
					onOk={handleEditSave}
					onCancel={() => setIsEditModalVisible(false)}
					okText="Save"
					className="[&_.ant-modal-content]:p-6 
					[&_.ant-btn-primary]:bg-blue-600 
					[&_.ant-btn-primary]:border-blue-600 
					[&_.ant-btn-primary:hover]:bg-blue-700 
					[&_.ant-btn-primary:hover]:border-blue-700
					[&_.ant-btn-default]:border-gray-200
					[&_.ant-btn-default:hover]:border-blue-600"
				>
					<div className="py-4">
						{isNoviUser ? (
							<TextEditor
								placeholder="Enter your business information"
								value={editTextInput}
								variables={variables}
								onChange={(text, variables) => {
									if (text) {
										setEditTextInput(text);
									}
									if (variables) {
										handleVariableChange(variables);
									}
								}}
							/>
						) : (
							<Input.TextArea
								autoSize={{ minRows: 8 }}
								maxLength={MAX_TEXT_CHARACTERS}
								value={editTextInput}
								onChange={(e) => setEditTextInput(e.target.value)}
								placeholder="Enter your business information"
								showCount
							/>
						)}
					</div>
				</Modal>
				<Modal
					title={
						<div className="flex items-center gap-3 pt-8 mx-4 md:pt-0">
							<DeleteOutlined className="text-red-600" />
							<span className="text-lg font-semibold">Confirm Delete</span>
						</div>
					}
					open={isDeleteModalVisible}
					onOk={(e) => {
						e?.stopPropagation();
						handleDelete();
					}}
					okButtonProps={{ loading: loading }}
					onCancel={(e) => {
						e?.stopPropagation();
						setIsDeleteModalVisible(false);
					}}
					okText="Delete"
					className="[&_.ant-modal-content]:p-6 
						[&_.ant-btn-primary]:!bg-red-600
						[&_.ant-btn-primary]:!border-red-600
						[&_.ant-btn-primary:hover]:!bg-red-700
						[&_.ant-btn-primary:hover]:!border-red-700
						[&_.ant-btn-default]:border-gray-200
						[&_.ant-btn-default:hover]:border-red-600"
				>
					<div className="py-4">
						<p>Are you sure you want to delete this from your Business Information?</p>
					</div>
				</Modal>
				<Modal
					title={
						<div className="flex items-center gap-3">
							<UploadOutlined className="text-blue-600" />
							<span className="text-lg font-semibold">Upload Document</span>
						</div>
					}
					open={isUploadModalVisible}
					onOk={handleUploadFile}
					onCancel={() => {
						setIsUploadModalVisible(false);
						setSelectedFile(null);
					}}
					confirmLoading={loading}
					okText="Upload"
					className="[&_.ant-modal-content]:p-6 
					[&_.ant-btn-primary]:bg-blue-600 
					[&_.ant-btn-primary]:border-blue-600 
					[&_.ant-btn-primary:hover]:bg-blue-700 
					[&_.ant-btn-primary:hover]:border-blue-700
					[&_.ant-btn-default]:border-gray-200
					[&_.ant-btn-default:hover]:border-blue-600"
				>
					<div className="py-4">
						<Alert
							message="Ready to Upload"
							description={`Selected file: ${selectedFile?.name}`}
							type="info"
							showIcon
							className="mb-4"
						/>

						<div className="mt-4">
							<div className="flex items-center gap-2">
								<LabelWithDescription label="Transform Instructions" />
								<Tooltip title="Specify how the AI should process this document. You can instruct it to extract specific parts, ignore certain sections, or limit the amount of data extracted. Example: 'Only extract pricing information' or 'Summarize in 500 words'">
									<InfoOutlined className="text-gray-400 text-sm cursor-help" />
								</Tooltip>
							</div>
							<Input.TextArea
								placeholder="optional: Provide instructions on how to extract or transform the content."
								className="mt-2"
								autoSize={{ minRows: 3, maxRows: 5 }}
								value={documentTransformInstructions}
								onChange={(e) => setDocumentTransformInstructions(e.target.value)}
							/>
						</div>
					</div>
				</Modal>

				<Modal
					title={
						<div className="flex justify-between items-center pt-8 mx-4 md:pt-0">
							<div className="flex items-center gap-3">
								<EyeOutlined className="text-blue-600" />
								<span className="text-lg font-semibold">View Content</span>
							</div>
							<Tag className="bg-blue-50 border-0 font-small px-2 mr-2 py-0.5 rounded-3xl">
								Not Editable
							</Tag>
						</div>
					}
					open={isViewModalVisible}
					onCancel={() => setIsViewModalVisible(false)}
					footer={
						<Button
							icon={<CopyOutlined />}
							onClick={() => navigator.clipboard.writeText(selectedText)}
							className="flex items-center border-blue-100 bg-blue-50 hover:bg-blue-100 hover:border-blue-200 text-blue-700"
						>
							Copy Text
						</Button>
					}
					className="[&_.ant-modal-content]:p-6 
					[&_.ant-btn-primary]:bg-blue-600 
					[&_.ant-btn-primary]:border-blue-600 
					[&_.ant-btn-primary:hover]:bg-blue-700 
					[&_.ant-btn-primary:hover]:border-blue-700
					[&_.ant-btn-default]:border-gray-200
					[&_.ant-btn-default:hover]:border-blue-600
					[&_.ant-btn-default:hover]:text-blue-600"
				>
					<div className="mt-4 p-4 bg-gray-50 rounded-lg">
						<Typography.Paragraph className="mb-0">
							<span
								dangerouslySetInnerHTML={{
									__html: formatTextWithHighlightedVariables(selectedText),
								}}
							/>
						</Typography.Paragraph>
					</div>
				</Modal>
			</div>
		</Spin>
	);
};
